<template>
  <div class="mobile-button-container">
    <div class="mobile-button hide">
      <main-button
        v-if="!checkTrialLimit()"
        depressed
        text="Add attendee"
        @click="
          goTo(
            isRegularOrganization()
              ? routes.organizer.createAccreditation
              : routes.sportOrganizer.createAccreditationSport,
            { eventId },
          )
        "
      />
    </div>
    <v-layout column>
      <v-flex class="title-screen" xs12>
        <span>Attendee list</span>
      </v-flex>
      <v-flex x4>
        <div class="title-description attendee-table-description">
          Here you can create, manage and search attendees. Also, you can print accreditations for
          one, more or all of them. You can customize the list by choosing which columns to display
          - up to 10 of them.
        </div>
      </v-flex>
      <v-flex class="mb-16">
        <v-tooltip v-if="checkTrialLimit()" class="desktop-button" right color="#979797">
          <main-button slot="activator" disabled text="Add attendee" />
          <span>You can create up to 20 accreditations in trial version</span>
        </v-tooltip>
        <main-button
          v-if="!checkTrialLimit()"
          depressed
          class="desktop-button"
          @click="
            goTo(
              isRegularOrganization()
                ? routes.organizer.createAccreditation
                : routes.sportOrganizer.createAccreditationSport,
              { eventId },
            )
          "
        >
          <span class="add-symbol">+</span>Add Attendee
        </main-button>
        <main-button v-if="!['952', '2444', '4531'].includes(eventId)" flat @click="printAll()">
          <img class="print-icon-style" src="/assets/Print.svg" alt="Print icon" />Print all
        </main-button>
      </v-flex>
      <div v-if="!$apollo.loading" class="attendee_table">
        <table-filters
          v-if="!showAlternateHeader"
          :visible-fields.sync="visibleFields"
          :all-fields="allFields"
          :initial-filters="{ quickSearch, ...filters }"
          :attendee-fields="attendeeFields"
          :categories="categories"
          :extra-modules="extraModules"
          :event-id="eventId"
          @quickSearch="handleQuickSearch"
          @filters="handleFilters"
        />
        <selected-attendees-header
          v-else
          :permission="permission"
          :selected="selected"
          :event-id="eventId"
          :user="user"
          @clear="selected = []"
          @print="print"
          @delete="showDeleteDialog = true"
          @openDemoModal="demoModal = true"
        />
        <div
          v-if="attendees.length === 0"
          class="no-attendees-message-container border__left border__right"
        >
          <span class="no-attendees-message">No matching records found.</span>
        </div>
        <table-list
          v-else
          :event-id="eventId"
          :organization-slug="organizationSlug"
          :selected.sync="selected"
          :attendees="attendees"
          :visible-fields="visibleFields"
          :attendee-fields="attendeeFields"
          :pagination="pagination"
          @click="
            goTo(
              isRegularOrganization()
                ? routes.organizer.editAccreditation
                : routes.sportOrganizer.editAccreditationSport,
              { accreditationId: $event },
            )
          "
          @sort="handleSort($event)"
          @attendeeDeleted="fetchAttendees(pagination, filters, quickSearch)"
        />
        <table-pagination
          :pagination="pagination"
          :total-attendees="totalAttendeeCount"
          @change="handlePagination($event)"
          @changePerPage="handlePagination($event)"
        />
      </div>
    </v-layout>
    <delete-dialog
      :content="`Are you sure you want to delete selected accreditations? This operation cannot be undone.`"
      :on-delete="deleteSelected"
      :show.sync="showDeleteDialog"
      title="Delete accreditations"
    />
    <simple-snackbar
      :open.sync="onPrintSnackbarOpen"
      :message-prop.sync="onPrintSnackbarMessage"
      :color="onPrintSnackbarColor"
    />
    <demo-modal v-if="demoModal" :user="loggedUser" @close="demoModal = false" />
  </div>
</template>

<script>
import { difference } from 'lodash'

import TableFilters from './attendee-table/filters.vue'
import TableList from './attendee-table/list.vue'
import TablePagination from './attendee-table/pagination.vue'
import SelectedAttendeesHeader from './attendee-table/selected-attendees-header.vue'

import { DELETE_ATTENDEES } from '../../graphql/accreditation.graphql'
import {
  GET_EVENT_ACCREDITATIONS_ONLY,
  GET_EVENT_DETAILS_WITH_FILTERED_ATTENDEE_FIELDS,
} from '../../graphql/event.graphql'
import { BULK_CREATE_PRINT, PRINT_ALL } from '../../graphql/print.graphql'
import { GET_LOGGED_USER } from '../../graphql/user.graphql'
import { getAttendeeByBarcode } from '../../http'
import { getVisibleFields, objectToArray, serverURL, setVisibleFields } from '../../utils'
import SimpleSnackbar from '../shared/snackbars/simple-snackbar.vue'
import DeleteDialog from './../shared/dialogs/delete-dialog.vue'
import { listOrderComparator } from './attendee-fields'
import DemoModal from '@/components/organizations/DemoModal.vue'
export default {
  name: 'accreditations-list-container',
  components: {
    TableFilters,
    TableList,
    TablePagination,
    SelectedAttendeesHeader,
    DeleteDialog,
    SimpleSnackbar,
    DemoModal,
  },
  props: {
    eventId: String,
    user: Object,
    organizationSlug: String,
  },
  data() {
    const { pagination, quickSearch, filters } = this.getInitialFromQueryParams()

    return {
      attendees: [],
      totalAttendeeCount: 0,
      categories: [],
      attendeeFields: [],
      eventAttendeeFields: {},
      extraModules: {},
      selected: [],
      quickSearch,
      pagination,
      permission: '',
      url: '',
      snackbar: false,
      filters,
      onCopyInviteSnackbar: false,
      showDeleteDialog: false,
      onPrintSnackbarOpen: false,
      onPrintSnackbarMessage: '',
      onPrintSnackbarColor: '',
      designSet: true,
      paginationLoading: false,
      managed: '',
      quickPrint: false,
      barcode: '',
      onBarcodeError: false,
      serverURL,
      visibleFields: [],
      allFields: [],
      loggedUser: '',
      demoModal: false,
    }
  },
  computed: {
    showAlternateHeader() {
      return this.selected && this.selected.length > 0
    },
  },
  async mounted() {
    await this.fetchAttendees(this.pagination, this.filters, { quickSearch: this.quickSearch })
  },
  apollo: {
    getEvent: {
      query: GET_EVENT_DETAILS_WITH_FILTERED_ATTENDEE_FIELDS,
      variables() {
        return {
          eventId: this.eventId,
          status: 'ALLOWED',
        }
      },
      manual: true,
      async result({ data, loading }) {
        if (!loading && data) {
          const {
            getEventWithFilteredAttendeeFields: {
              builder,
              extraModules,
              categories,
              managed,
              attendeeFields,
              quickPrint,
            },
            getAccreditationCount,
          } = data
          this.totalAttendeeCount = getAccreditationCount
          this.categories = categories || []
          this.attendeeFields = attendeeFields
          this.attendeeFields['Attendee name'] = {
            type: 'text',
            id: 'Attendee name',
          }
          this.eventAttendeeFields = objectToArray(attendeeFields, 'name').sort(listOrderComparator)
          this.extraModules = extraModules || {}
          this.designSet = !!builder
          this.managed = managed
          this.quickPrint = quickPrint
          this.allFields = this.eventAttendeeFields.map(f => f.name)
          this.allFields = this.allFields.filter(af => {
            return (
              af !== 'First name' && af !== 'Last name' && af !== 'First Name' && af !== 'Last Name'
            )
          })
          if (this.extraModules.categoriesAndZones) {
            this.allFields = [...this.eventAttendeeFields.map(f => f.name), 'Category']
          }
          const localStorageFields = getVisibleFields(this.eventId)
          if (localStorageFields.length > 0) {
            if (difference(localStorageFields, this.allFields).length > 0) {
              // if there is a field in my local, and not in definition, it means that other user deleted it
              this.visibleFields = this.allFields
            } else {
              this.visibleFields = localStorageFields
            }
          } else {
            this.visibleFields = this.allFields
          }
          this.visibleFields = [
            'Attendee name',
            ...this.visibleFields.filter(vf => vf !== 'Attendee name'),
            'Created At',
          ]
          this.visibleFields = this.visibleFields.filter(vf => {
            return (
              vf !== 'First name' && vf !== 'Last name' && vf !== 'First Name' && vf !== 'Last Name'
            )
          })
          setVisibleFields([...this.visibleFields.filter(f => !f.includes('?'))], this.eventId)
          const {
            data: {
              me: { email },
            },
          } = await this.$apollo.query({ query: GET_LOGGED_USER })
          this.loggedUser = email
        }
      },
    },
  },
  methods: {
    async handlePagination({ page, per_page }) {
      const { page: currentPage, per_page: currentPerPage } = this.pagination
      if (currentPage > page && currentPage === 1) return
      if (page > currentPage && !(this.totalAttendeeCount - currentPerPage * currentPage > 0))
        return

      this.pagination = {
        ...this.pagination,
        per_page: per_page === 'All' ? -1 : per_page,
        page: page,
      }

      this.addPaginationToRoute(this.pagination)

      await this.fetchAttendees(this.pagination, this.filters, this.quickSearch)
    },
    async handleFilters(filters) {
      this.addFiltersToRoute(filters)
      this.filters = filters

      await this.fetchAttendees(this.pagination, this.filters, this.quickSearch)
    },
    async handleQuickSearch(quickSearch) {
      this.pagination = { ...this.pagination, page: 1 }
      this.addFiltersToRoute(quickSearch)
      this.quickSearch = quickSearch

      await this.fetchAttendees(this.pagination, this.filters, this.quickSearch)
    },
    async handleSort(sortBy) {
      const { per_page, page, sortBy: thisSortBy, descending } = this.pagination
      const pagination = {
        page,
        per_page,
        sortBy,
        descending: sortBy === thisSortBy ? !descending : false,
      }
      this.addPaginationToRoute(pagination)
      this.pagination = pagination

      await this.fetchAttendees(
        pagination,
        this.filters,
        typeof this.quickSearch === 'string' ? { quickSearch: this.quickSearch } : this.quickSearch,
      )
    },
    getInitialFromQueryParams() {
      const { per_page, page, sortBy, descending, quickSearch, ...fieldFilters } = this.$route.query

      return {
        pagination: {
          page: parseInt(page, 10) || 1,
          per_page: parseInt(per_page, 10) || 25,
          sortBy: sortBy,
          descending: descending === 'true' ? true : false,
        },
        quickSearch: quickSearch || '',
        filters: fieldFilters || {},
      }
    },
    addPaginationToRoute({ page, per_page, sortBy, descending }) {
      this.$router.replace({
        query: {
          ...this.$route.query,
          per_page,
          page,
          sortBy,
          descending,
        },
      })
    },
    addFiltersToRoute(filters) {
      const filtersWithValueArray = Object.entries(filters).filter(f => f[1]) // remove entries with no value

      const filtersWithValue = filtersWithValueArray.reduce((acc, e) => {
        //handle problem with '/' in url
        if (e[0].includes('/')) {
          acc[e[0].replaceAll('/', '-')] = e[1]
        } else {
          acc[e[0]] = e[1]
        }

        return acc
      }, {})

      this.$router.replace({
        query: {
          ...filtersWithValue,
          ...this.pagination,
        },
      })
    },
    async onBarcodeScanned() {
      const { data, errors } = await getAttendeeByBarcode(this.barcode, this.eventId)
      if (data && !errors) {
        const { id } = data.getAttendeeByBarcode
        this.goTo(
          this.isRegularOrganization()
            ? this.routes.organizer.editAccreditation
            : this.routes.sportOrganizer.editAccreditationSport,
          { accreditationId: id },
        )
      } else {
        this.onBarcodeError = true
      }
      this.barcode = ''
    },
    toArray(array) {
      return Array.isArray(array) ? array : [array]
    },
    areFiltersEmpty() {
      return Object.values(this.filters).some(f => f === undefined || f.length === 0)
    },
    checkTrialLimit() {
      return this.user.type === 'TRIAL' ? this.totalAttendeeCount >= 20 : false
    },
    async print() {
      const status = await this.$apollo.mutate({
        mutation: BULK_CREATE_PRINT,
        variables: {
          accreditationIds: this.selected,
          id: this.eventId,
        },
      })
      this.afterPrint(status.data.bulkCreatePrint)
    },
    afterPrint() {
      this.selected = []
      this.onPrintSnackbarOpen = true
      this.onPrintSnackbarMessage = 'We have sent you an email with your accreditations'
      this.onPrintSnackbarColor = 'primary'
    },
    async printAll() {
      this.$apollo.mutate({
        mutation: PRINT_ALL,
        variables: {
          id: this.eventId,
        },
      })
      this.afterPrint()
    },
    async fetchAttendees(pagination, filters, quickSearch) {
      this.paginationLoading = true
      const { data } = await this.$apollo.query({
        query: GET_EVENT_ACCREDITATIONS_ONLY,
        variables: {
          eventId: this.eventId,
          status: 'ALLOWED',
          quickSearch: (quickSearch && quickSearch.quickSearch) || '',
          visibleFields: this.visibleFields,
          pagination: pagination || this.pagination,
          filters: filters || this.filters || {},
        },
      })
      if (!data) {
        return
      }
      const { attendees, attendeeCount } = data.getEventAccreditations
      this.attendees = attendees
      this.attendees.map(a => {
        if (a.fields) {
          a.fields['Attendee name'] =
            (a.fields['First name'] || a.fields['Ime']) +
            ' ' +
            (a.fields['Last name'] || a.fields['Prezime'])
        } else {
          a.fields = { 'Attendee name': 'Unknown attendee' }
        }
      })

      this.totalAttendeeCount = parseInt(attendeeCount, 10)
      this.paginationLoading = false
    },
    async deleteSelected() {
      await this.$apollo.mutate({
        mutation: DELETE_ATTENDEES,
        variables: { attendeeIds: this.selected, eventId: this.eventId },
      })
      await this.fetchAttendees()
      this.totalAttendeeCount = this.totalAttendeeCount - this.selected.length
      this.selected = []
    },
    isRegularOrganization() {
      return this.$route.path.includes('/organizations/')
    },
  },
}
</script>

<style scoped>
.attendee-table-description {
  max-width: 900px;
  margin-bottom: 28px;
}

.no-attendees-message-container {
  display: flex;
  align-items: center;
  justify-content: center;
}

.no-attendees-message {
  color: #979797;
  padding: 20px;
}

.border__left {
  border-left: 2px solid #f5f5f5;
}

.border__right {
  border-right: 2px solid #f5f5f5;
}

ul {
  list-style-type: none;
}

.err-line {
  margin-bottom: 8px;
}

.err-line span {
  font-weight: bold;
}

.add-symbol {
  font-size: 1.875rem;
  margin-right: 0.8125rem;
  font-weight: 200;
}

.print-icon-style {
  margin-right: 0.5rem;
}

.title-screen {
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 600;
  font-size: 24px;
  letter-spacing: -0.03em;
  font-feature-settings: 'pnum' on, 'lnum' on;
  color: #ff4c00;
  margin-bottom: 1.5rem;
}

@media screen and (max-width: 800px) {
  .title-screen {
    display: none;
  }

  .mobile-button-container {
    margin-top: 1.5rem;
  }
}
</style>
